/**
 * useRoleAccess — Centralized role-based access control hook
 *
 * Uses dynamic permissions from user.permissions JSON configured on /roles.
 * Synchronized 100% with packages/trpc/src/permissions.ts and apps/api/src/common/utils/permission.util.ts.
 */

import { useAuthStore } from '@/stores/auth.store';
import { useMemo } from 'react';

export const FRONTEND_MODULE_ALIAS_MAP: Record<string, string[]> = {
    'customers': ['customers', 'customer'],
    'quotations': ['quotations', 'quotation'],
    'orders': ['orders', 'order', 'sales-orders', 'salesOrders'],
    'deliveries': ['deliveries', 'delivery'],
    'products': ['products', 'product'],
    'product-types': ['product-types', 'productTypes', 'product_types', 'products'],
    'production-orders': ['production-orders', 'productionOrders', 'production_orders', 'production'],
    'productionOrders': ['production-orders', 'productionOrders', 'production_orders', 'production'],
    'production_orders': ['production-orders', 'productionOrders', 'production_orders', 'production'],
    'production': ['production-orders', 'productionOrders', 'production_orders', 'production', 'machine-logs'],
    'material-planning': ['material-planning', 'materialPlanning', 'material_planning', 'materials', 'production-orders'],
    'materialPlanning': ['material-planning', 'materialPlanning', 'material_planning', 'materials', 'production-orders'],
    'process-planning': ['process-planning', 'processPlanning', 'process_planning', 'production-orders', 'processes'],
    'processPlanning': ['process-planning', 'processPlanning', 'process_planning', 'production-orders', 'processes'],
    'purchasing': ['purchasing', 'purchase-orders', 'purchaseOrders', 'purchase_orders'],
    'purchaseOrders': ['purchasing', 'purchase-orders', 'purchaseOrders', 'purchase_orders'],
    'purchase_orders': ['purchasing', 'purchase-orders', 'purchaseOrders', 'purchase_orders'],
    'outsource-orders': ['outsource-orders', 'outsourceOrders', 'outsource_orders', 'outsourcing', 'outsource-pos'],
    'outsourceOrders': ['outsource-orders', 'outsourceOrders', 'outsource_orders', 'outsourcing', 'outsource-pos'],
    'vendors': ['vendors', 'vendor', 'purchasing'],
    'receipts': ['receipts', 'receipt', 'finance', 'cashbook'],
    'debts': ['debts', 'debt', 'finance', 'reconciliations', 'ar', 'ap'],
    'finance': ['finance', 'receipts', 'debts', 'reconciliations'],
    'reconciliations': ['reconciliations', 'debts', 'finance', 'receipts'],
    'machine-logs': ['machine-logs', 'machineLogs', 'machine_logs', 'production-logs', 'productionLogs', 'production_logs', 'mes', 'production'],
    'production-logs': ['production-logs', 'productionLogs', 'machine-logs', 'machineLogs', 'mes', 'production'],
    'productionLogs': ['production-logs', 'productionLogs', 'machine-logs', 'machineLogs', 'mes', 'production'],
    'wip-transfer': ['wip-transfer', 'wipTransfer', 'wip_transfer', 'wip-transfers', 'production'],
    'specs': ['specs', 'spec', 'technical', 'designs', 'products'],
    'designs': ['designs', 'design', 'specs', 'layouts', 'technical'],
    'layouts': ['layouts', 'layout', 'prepress', 'print-layouts', 'technical'],
    'prepress': ['prepress', 'layouts', 'specs', 'plates', 'ctp-logs', 'technical'],
    'molds': ['molds', 'mold', 'die-cuts', 'dieCuts', 'tooling', 'technical'],
    'plates': ['plates', 'plate', 'ctp-logs', 'technical'],
    'ctp-logs': ['ctp-logs', 'ctpLogs', 'ctp_logs', 'plates', 'technical'],
    'technical': ['technical', 'specs', 'layouts', 'molds', 'plates', 'ctp-logs', 'processes'],
    'qc-materials': ['qc-materials', 'qcMaterials', 'qc_materials', 'qcCheck', 'quality'],
    'qcMaterials': ['qc-materials', 'qcMaterials', 'qc_materials', 'qcCheck', 'quality'],
    'qc-process': ['qc-process', 'qcProcess', 'qc_process', 'qcCheck', 'quality'],
    'qcProcess': ['qc-process', 'qcProcess', 'qc_process', 'qcCheck', 'quality'],
    'qc-final': ['qc-final', 'qcFinal', 'qc_final', 'qcCheck', 'quality', 'qc-templates', 'qcTemplates'],
    'qcFinal': ['qc-final', 'qcFinal', 'qc_final', 'qcCheck', 'quality', 'qc-templates', 'qcTemplates'],
    'qcCheck': ['qcCheck', 'qc-materials', 'qc-process', 'qc-final', 'quality'],
    'quality': ['quality', 'qc-materials', 'qc-process', 'qc-final', 'ncr', 'qcCheck'],
    'ncr': ['ncr', 'capa', 'quality', 'qc-final'],
    'qc-templates': ['qc-templates', 'qcTemplates', 'qc-final', 'quality'],
    'material-warehouse': ['material-warehouse', 'materialWarehouse', 'warehouse', 'warehouses', 'inventory', 'materials'],
    'fg-inventory': ['fg-inventory', 'fgInventory', 'warehouse', 'warehouses', 'inventory'],
    'warehouse': ['warehouse', 'warehouses', 'material-warehouse', 'fg-inventory', 'inventory'],
    'warehouses': ['warehouses', 'warehouse', 'material-warehouse', 'fg-inventory', 'inventory'],
    'inventory': ['inventory', 'material-warehouse', 'fg-inventory', 'warehouse', 'warehouses', 'materials'],
    'materials': ['materials', 'material', 'material-warehouse'],
    'material-types': ['material-types', 'materialTypes', 'material_types', 'materials'],
    'company-info': ['company-info', 'companyInfo', 'company_info', 'configs'],
    'users': ['users', 'user'],
    'roles': ['roles', 'role'],
    'machines': ['machines', 'machine'],
    'processes': ['processes', 'process', 'waste-matrix', 'wasteMatrix'],
    'waste-matrix': ['waste-matrix', 'wasteMatrix', 'processes', 'configs'],
    'workflows': ['workflows', 'workflow', 'configs'],
    'configs': ['configs', 'config', 'settings'],
    'settings': ['settings', 'configs', 'config'],
    'trash': ['trash', 'configs'],
    'feedback': ['feedback', 'feedbacks', 'my-requests'],
    'reports': ['reports', 'report', 'dashboard', 'analytics'],
};

function normalizeFrontendAction(actionId: string): string[] {
    const act = (actionId || '').trim().toLowerCase();
    if (act === 'edit' || act === 'update') return ['update', 'edit'];
    if (act === 'read' || act === 'view') return ['view', 'read'];
    if (act === 'check' || act === 'review') return ['review', 'check', 'approve', 'update'];
    if (act === 'restore') return ['restore', 'update', 'delete'];
    return [act];
}

function resolveFrontendModulePermission(
    permissions: Record<string, any>,
    moduleId: string
): { actions: string[]; dataScope?: string; financialScope?: string } | undefined {
    if (!permissions || typeof permissions !== 'object') return undefined;
    if (permissions[moduleId] && typeof permissions[moduleId] === 'object') {
        return permissions[moduleId];
    }
    const candidates = FRONTEND_MODULE_ALIAS_MAP[moduleId] || [moduleId];
    for (const candidate of candidates) {
        const entry = permissions[candidate];
        if (entry && typeof entry === 'object' && Array.isArray(entry.actions)) {
            return entry;
        }
    }
    return undefined;
}

export function useRoleAccess() {
    const user = useAuthStore(s => s.user);

    return useMemo(() => {
        const role = (user?.role || '').toUpperCase();
        let rawPermissions: any = user?.permissions || {};

        if (typeof rawPermissions === 'string') {
            try {
                rawPermissions = JSON.parse(rawPermissions);
            } catch {
                rawPermissions = {};
            }
        }

        let permissions: Record<string, any> = {};

        // Handle legacy Array format
        if (Array.isArray(rawPermissions)) {
            const newPerms: Record<string, any> = {};
            if (rawPermissions.includes('*')) {
                newPerms['*'] = { actions: ['*'], dataScope: 'ALL', financialScope: 'ALL' };
            } else {
                rawPermissions.forEach((p: string) => {
                    if (typeof p === 'string') {
                        const [mod, rawAct] = p.split('.');
                        const act = rawAct === 'edit' ? 'update' : rawAct === 'check' ? 'review' : rawAct;
                        if (mod && act) {
                            if (!newPerms[mod]) newPerms[mod] = { actions: [], dataScope: 'ALL', financialScope: 'ALL' };
                            if (!newPerms[mod].actions.includes(act)) newPerms[mod].actions.push(act);
                        }
                    }
                });
            }
            permissions = newPerms;
        } else if (typeof rawPermissions === 'object' && rawPermissions !== null) {
            permissions = rawPermissions;
        }

        // Full access check (for ADMIN or full wildcard)
        const hasFullAccess = Boolean(permissions['*']) || role === 'ADMIN' || role === 'SUPERADMIN';

        /**
         * Check if user has specific action permission in a module
         */
        const hasPermission = (moduleId: string, actionId: string): boolean => {
            if (!user) return false;
            if (hasFullAccess) return true;

            const modPerm = resolveFrontendModulePermission(permissions, moduleId);
            const candidateActions = normalizeFrontendAction(actionId);

            if (!modPerm || !Array.isArray(modPerm.actions)) {
                // Vai trò chưa cấu hình quyền = không có quyền (đồng bộ máy chủ, 04/11/2026)
                return false;
            }
            if (modPerm.actions.includes('*')) return true;
            return candidateActions.some((act) => modPerm.actions.includes(act));
        };

        /**
         * Get data scope for a module (NONE, PERSONAL, TEAM, ALL)
         */
        const getDataScope = (moduleId: string): 'NONE' | 'PERSONAL' | 'TEAM' | 'ALL' => {
            if (hasFullAccess) return 'ALL';
            const modPerm = resolveFrontendModulePermission(permissions, moduleId);
            if (modPerm) {
                const hasAnyAction = Array.isArray(modPerm.actions) && modPerm.actions.length > 0;
                const scope = modPerm.dataScope as 'NONE' | 'PERSONAL' | 'TEAM' | 'ALL' | undefined;
                if (scope && !(scope === 'NONE' && hasAnyAction)) return scope;
                if (hasAnyAction) return 'ALL';
                return 'NONE';
            }
            return 'ALL';
        };

        /**
         * Get financial scope for a module (NONE, PERSONAL, TEAM, DEPARTMENT, ALL)
         */
        const getFinancialScope = (moduleId: string): 'NONE' | 'PERSONAL' | 'TEAM' | 'DEPARTMENT' | 'ALL' => {
            if (hasFullAccess) return 'ALL';
            const modPerm = resolveFrontendModulePermission(permissions, moduleId);
            if (modPerm) {
                const hasAnyAction = Array.isArray(modPerm.actions) && modPerm.actions.length > 0;
                const scope = modPerm.financialScope as 'NONE' | 'PERSONAL' | 'TEAM' | 'DEPARTMENT' | 'ALL' | undefined;
                if (scope && !(scope === 'NONE' && hasAnyAction)) return scope;
                if (hasAnyAction) return 'ALL';
                return 'NONE';
            }
            return 'ALL';
        };

        const isAdmin = role === 'ADMIN' || role === 'SUPERADMIN' || hasFullAccess;

        // Dynamic helpers mapped to role permissions
        const canViewAllData = getDataScope('orders') === 'ALL';
        const canAddMasterData = hasPermission('customers', 'create') || hasPermission('products', 'create');

        const canEditOrder = (orderStatus: string) => {
            if (isAdmin) return true;
            if (!hasPermission('orders', 'update')) return false;
            if (['DRAFT', 'PENDING', 'WAITING_PRE_PRESS', 'CONFIRMED', 'APPROVED'].includes(orderStatus)) return true;
            return hasPermission('orders', 'approve');
        };

        return {
            user,
            userId: user?.id || '',
            role,

            // New dynamic API
            hasFullAccess,
            hasPermission,
            getDataScope,
            getFinancialScope,

            // Legacy backward compatible API
            isAdmin,
            canViewAllData,
            canAddMasterData,
            canEditOrder,
        };
    }, [user]);
}
